import { type RefObject, useRef } from 'react';
import { releasesSwipe, swipesFrom, useHorizontalDrag } from './use-horizontal-drag';

/** How long the revealed screen takes to settle (the screens' own transition). */
const SETTLE_MS = 300;

/**
 * Drags a screen in from the right edge under the finger as it swipes left on `ref`: released
 * far enough (or flicked), it settles in and `onReveal` runs; otherwise it slides back out.
 * The screen sits out with a `translate` class; the drag holds it with an inline `translate`.
 */
export function useSwipeReveal(
  ref: RefObject<HTMLElement | null>,
  screen: RefObject<HTMLElement | null>,
  onReveal: () => void,
  enabled: boolean,
): void {
  const reveal = useRef(onReveal);
  reveal.current = onReveal;
  const settle = (translate: string) => {
    const element = screen.current;
    if (element === null) return;
    element.style.transition = '';
    element.style.translate = translate;
    // Once settled, its class holds it.
    window.setTimeout(() => {
      element.style.translate = '';
    }, SETTLE_MS);
  };
  useHorizontalDrag(ref, {
    enabled,
    accepts: (event) => swipesFrom(event.target),
    allows: (dx) => dx < 0,
    onDrag: (dx) => {
      const element = screen.current;
      if (element === null) return;
      element.style.transition = 'none';
      element.style.translate = `${Math.max(0, element.clientWidth + dx)}px 0`;
    },
    onRelease: (dx, velocity) => {
      const width = screen.current?.clientWidth ?? 0;
      if (releasesSwipe({ dx, width, velocity })) {
        settle('0px 0');
        reveal.current();
      } else settle('100% 0');
    },
    onCancel: () => settle('100% 0'),
  });
}
